/**
 * Google Tag Manager - dataLayer utility
 *
 * Uso:
 *   import { gtmEvent } from "@/lib/gtm";
 *   gtmEvent("add_to_cart", { ecommerce: { ... } });
 *
 * El evento se empuja al dataLayer (GTM Container ID en VITE_GTM_ID) y además
 * se reenvía directo a GA4 con gtag: hoy no hay contenedor de GTM configurado,
 * así que los eventos de ecommerce se quedaban sin llegar a GA4.
 *
 * ⚠️ Si algún día se activa VITE_GTM_ID y el contenedor reenvía ecommerce a
 * GA4, quitar el emit directo de abajo para no duplicar el conteo.
 */

import { trackEvent } from "@/lib/analytics";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

/** Push un evento al dataLayer de GTM + emit directo a GA4 */
export const gtmEvent = (event: string, data?: Record<string, unknown>) => {
  window.dataLayer = window.dataLayer || [];
  // Limpiar ecommerce previo para evitar datos cruzados entre eventos
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ...data });

  // GA4: los "recommended events" esperan los campos planos (items al mismo
  // nivel), no anidados bajo `ecommerce`.
  if (!data) {
    trackEvent(event);
    return;
  }
  const { ecommerce, ...rest } = data;
  const params: Record<string, unknown> =
    ecommerce && typeof ecommerce === "object"
      ? { ...rest, ...(ecommerce as Record<string, unknown>) }
      : data;
  trackEvent(event, params);
};

/** Mapea un item del carrito/producto al formato GA4 ecommerce */
export const mapItemToGA4 = (item: {
  id: string;
  name: string;
  price: number;
  quantity?: number;
  type?: string;
}) => ({
  item_id: item.id,
  item_name: item.name,
  affiliation: "VIVI con Amor y Arte",
  price: item.price,
  quantity: item.quantity ?? 1,
  item_category: item.type ?? "físico",
  currency: "USD",
});
